import { APIError } from '../../../utils/api'
import { getErrorMessage } from '../../../utils/getErrorMessage'

type AxiosLikeError = {
  response?: {
    data?: unknown
  }
}

/**
 * Extracts a user-facing error message from the two distinct error shapes the
 * imported (shoebox) upload flow can throw, so the surfaced message is always the
 * server one when available:
 *
 * - `uploadFile` (billy-api) rejects with an {@link APIError} whose `.message`
 *   already composes the server `errorMessage`/`message`/`validationErrors`. We
 *   delegate to the shared {@link getErrorMessage} rather than re-reading
 *   `error.body.errorMessage` by hand.
 * - `createImportedInvoice` goes through apiZervant (axios) and rejects with a
 *   raw AxiosError (NOT an `APIError`), so `getErrorMessage` can't read it. The
 *   rest-invoice error body lands on `error.response.data` — normally `{ message }`,
 *   sometimes `{ errorMessage }` or a plain string.
 *
 * Returns `undefined` when neither shape carries a message, so the caller can fall
 * back to its own generic copy.
 */
export function getImportedUploadErrorMessage(error: unknown): string | undefined {
  if (error instanceof APIError) {
    return getErrorMessage(error)
  }

  if (typeof error === 'object' && error !== null) {
    const responseData = (error as AxiosLikeError).response?.data

    if (typeof responseData === 'string') {
      return responseData || undefined
    }

    if (typeof responseData === 'object' && responseData !== null) {
      const { message, errorMessage } = responseData as { message?: string; errorMessage?: string }
      return message || errorMessage || undefined
    }
  }

  return undefined
}
